/**
 * vehicle_fx.ts — the hovercraft's ENGINE + DUST effects, fitted per model.
 *
 *  · ENGINES — every nozzle from the model's visual signature is placed on the
 *    hull's TAIL line (measured from the hull's local bounding box, so a GLB or
 *    the fallback hull both fit): a hot core sprite at the mouth and an
 *    additive exhaust cone behind it. The engines read as ON the moment you
 *    are mounted (a pilot glow), swell with throttle + speed (a jet spooling
 *    up), and under SHIFT boost / a drift mini-turbo the AFTERBURNER lights:
 *    the plume stretches ×2.2, shifts to the boost colour and a wide short
 *    flare ring appears at the mouth — the booster indicator.
 *  · DUST — above HOVER_DUST_SPEED on the ground the skirt throws VERY FINE
 *    dirt from its rear corners: a pooled world-space Points cloud (no draw
 *    call per mote), soft-disc motes that drift back, rise, decelerate and
 *    fade; drifting kicks harder. Over open water the same pool throws spray.
 *
 * Pure three scene-graph code — no renderer calls — so the fit, the nozzle
 * layout and the emission gates are provable headlessly.
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { attribute } from 'three/tsl';
import { hovercraftVisual, type HovercraftVisual } from './hovercraft_visuals.js';
import { VEHICLE_SPEED_SCALE, HOVER_WATER_TOP } from './hover_physics.js';

// Emission gates ride VEHICLE_SPEED_SCALE with every other speed-dimensioned
// vehicle constant. WHY (2026-09-14): these were literals left behind when the
// craft were slowed to a quarter of their top speeds. Dust only appeared near
// flat out, and SPRAY WAS UNREACHABLE — its 10 m/s gate sat above
// HOVER_WATER_TOP (30 × 0.25 = 7.5 m/s), the hard ceiling for any craft over
// open water, so no hovercraft could ever throw spray.
export const HOVER_DUST_SPEED = 30 * VEHICLE_SPEED_SCALE;   // m/s — the skirt starts throwing dirt here
export const HOVER_SPRAY_SPEED = 10 * VEHICLE_SPEED_SCALE;  // m/s — spray over water (must stay under HOVER_WATER_TOP)
const DUST_POOL = 720;
const DUST_GRAVITY = 4.0;                // m/s² (fine motes settle slowly)
const DUST_DRAG = 1.6;                   // /s velocity bleed
const BOOST_PLUME_MULT = 2.2;

export interface VehicleFxInputs {
  dt: number;
  speed: number;      // horizontal m/s
  throttle: number;   // 0..1 (forward thrust demand)
  boost: boolean;     // SHIFT afterburner
  turbo: boolean;     // drift mini-turbo firing
  grounded: boolean;  // skirt on the surface (not airborne)
  onWater: boolean;   // riding a wake
  drifting: boolean;
}

export interface NozzleRig {
  group: THREE.Group;     // at the nozzle mouth, +Z = exhaust direction (hull tail)
  plume: THREE.Mesh;      // exhaust cone (scale.z = length)
  flare: THREE.Mesh;      // afterburner ring (boost only)
  core: THREE.Sprite;     // hot core at the mouth
  plumeMat: THREE.MeshBasicMaterial;
  flareMat: THREE.MeshBasicMaterial;
  coreMat: THREE.SpriteMaterial;
}

let discTex: THREE.DataTexture | null = null;
/** A soft radial disc (procedural — no canvas, so Node harnesses can build it). */
export function softDiscTexture(): THREE.DataTexture {
  if (discTex) return discTex;
  const n = 32;
  const data = new Uint8Array(n * n * 4);
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const dx = (x + 0.5) / n - 0.5, dy = (y + 0.5) / n - 0.5;
      const d = Math.min(1, Math.hypot(dx, dy) * 2);
      const a = Math.max(0, 1 - d);
      const v = Math.round(255 * a * a * (3 - 2 * a)); // smoothstep falloff
      const i = (y * n + x) * 4;
      data[i] = data[i + 1] = data[i + 2] = 255;
      data[i + 3] = v;
    }
  }
  discTex = new THREE.DataTexture(data, n, n, THREE.RGBAFormat);
  discTex.needsUpdate = true;
  discTex.minFilter = THREE.LinearFilter;
  discTex.magFilter = THREE.LinearFilter;
  discTex.userData.shared = true;
  return discTex;
}

/** The hull's bounding box in ITS OWN frame (children's world boxes pulled back
 *  through the hull's inverse world matrix) — a rig parented anywhere fits. */
export function localHullBox(hull: THREE.Object3D): THREE.Box3 {
  hull.updateMatrixWorld(true);
  const inv = hull.matrixWorld.clone().invert();
  const out = new THREE.Box3();
  const tmp = new THREE.Box3();
  const m = new THREE.Matrix4();
  hull.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || !mesh.geometry) return;
    if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
    if (!mesh.geometry.boundingBox) return;
    m.multiplyMatrices(inv, mesh.matrixWorld);
    tmp.copy(mesh.geometry.boundingBox).applyMatrix4(m);
    out.union(tmp);
  });
  if (out.isEmpty()) out.set(new THREE.Vector3(-0.7, 0, -1.3), new THREE.Vector3(0.7, 0.8, 1.3));
  return out;
}

/** Nozzle mouth positions on the tail line for a model + hull box (hull frame: nose −Z, tail +Z). */
export function nozzleLayout(vis: HovercraftVisual, box: THREE.Box3): THREE.Vector3[] {
  const w = box.max.x - box.min.x, h = box.max.y - box.min.y, len = box.max.z - box.min.z;
  const tailZ = box.max.z - len * 0.04;
  const y = box.min.y + h * 0.42;
  const n = Math.max(1, vis.nozzles);
  const out: THREE.Vector3[] = [];
  for (let i = 0; i < n; i++) {
    const t = n === 1 ? 0 : (i / (n - 1)) * 2 - 1;
    out.push(new THREE.Vector3(t * (w / 2) * vis.spreadFrac, y, tailZ));
  }
  return out;
}

/** Dust throw points: the skirt's rear corners + centre, on the hull's bottom. */
export function dustLayout(box: THREE.Box3): THREE.Vector3[] {
  const w = box.max.x - box.min.x, len = box.max.z - box.min.z;
  const y = box.min.y + 0.05;
  const z = box.min.z + len * 0.78;
  return [new THREE.Vector3(-w * 0.34, y, z), new THREE.Vector3(0, y, z + len * 0.1), new THREE.Vector3(w * 0.34, y, z)];
}

export class VehicleFx {
  readonly vis: HovercraftVisual;
  readonly box: THREE.Box3;
  readonly nozzles: NozzleRig[] = [];
  readonly dust: THREE.Points;
  private readonly dustPts: THREE.Vector3[];
  private readonly pos: Float32Array;
  private readonly col: Float32Array;
  private readonly fade: Float32Array;
  private readonly size: Float32Array;
  private readonly vel: Float32Array;
  private readonly life: Float32Array;
  private readonly maxLife: Float32Array;
  private readonly posAttr: THREE.BufferAttribute;
  private readonly colAttr: THREE.BufferAttribute;
  private readonly fadeAttr: THREE.BufferAttribute;
  private readonly sizeAttr: THREE.BufferAttribute;
  private alive = 0;
  private emitAcc = 0;
  private t = 0;
  private plumeLen = 0;
  private readonly _v = new THREE.Vector3();
  private readonly _q = new THREE.Quaternion();
  private readonly _s = new THREE.Vector3();
  private readonly _p = new THREE.Vector3();
  private readonly _fwd = new THREE.Vector3();
  private readonly dustMat: THREE.PointsNodeMaterial;
  private disposed = false;

  constructor(modelKey: string, readonly hull: THREE.Object3D, readonly world: THREE.Object3D) {
    this.vis = hovercraftVisual(modelKey);
    this.box = localHullBox(hull);
    const disc = softDiscTexture();
    // ── Engines ──
    for (const p of nozzleLayout(this.vis, this.box)) {
      const group = new THREE.Group();
      group.position.copy(p);
      const r = this.vis.nozzleR;
      // Exhaust cone: wide at the mouth (z=0), tapering behind (+Z); unit length.
      const plumeGeo = new THREE.CylinderGeometry(r * 0.12, r, 1, 12, 1, true);
      plumeGeo.translate(0, 0.5, 0);
      plumeGeo.rotateX(Math.PI / 2);
      const plumeMat = new THREE.MeshBasicMaterial({ color: this.vis.color, transparent: true, opacity: 0.5, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide, toneMapped: false });
      const plume = new THREE.Mesh(plumeGeo, plumeMat);
      plume.scale.z = 0.001;
      // Afterburner flare: a short wide ring at the mouth, boost only.
      const flareGeo = new THREE.CylinderGeometry(r * 1.9, r * 1.1, 1, 14, 1, true);
      flareGeo.translate(0, 0.5, 0);
      flareGeo.rotateX(Math.PI / 2);
      const flareMat = new THREE.MeshBasicMaterial({ color: this.vis.boostColor, transparent: true, opacity: 0.0, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide, toneMapped: false });
      const flare = new THREE.Mesh(flareGeo, flareMat);
      flare.scale.z = 0.001;
      flare.visible = false;
      // Hot core at the mouth.
      const coreMat = new THREE.SpriteMaterial({ map: disc, color: this.vis.color, transparent: true, opacity: 0.95, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false });
      markMrtOverlay(plumeMat); markMrtOverlay(flareMat); markMrtOverlay(coreMat); // exhaust over the hull and the ground
      const core = new THREE.Sprite(coreMat);
      core.scale.setScalar(r * 2.2);
      group.add(plume, flare, core);
      for (const o of [plume, flare, core]) { o.frustumCulled = false; o.renderOrder = 6; }
      hull.add(group);
      this.nozzles.push({ group, plume, flare, core, plumeMat, flareMat, coreMat });
    }
    // ── Dust pool ──
    this.dustPts = dustLayout(this.box);
    this.pos = new Float32Array(DUST_POOL * 3);
    this.col = new Float32Array(DUST_POOL * 3);
    this.fade = new Float32Array(DUST_POOL);
    this.size = new Float32Array(DUST_POOL);
    this.vel = new Float32Array(DUST_POOL * 3);
    this.life = new Float32Array(DUST_POOL);
    this.maxLife = new Float32Array(DUST_POOL);
    const geo = new THREE.BufferGeometry();
    this.posAttr = new THREE.BufferAttribute(this.pos, 3); this.posAttr.setUsage(THREE.DynamicDrawUsage);
    this.colAttr = new THREE.BufferAttribute(this.col, 3); this.colAttr.setUsage(THREE.DynamicDrawUsage);
    this.fadeAttr = new THREE.BufferAttribute(this.fade, 1); this.fadeAttr.setUsage(THREE.DynamicDrawUsage);
    this.sizeAttr = new THREE.BufferAttribute(this.size, 1); this.sizeAttr.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('position', this.posAttr);
    geo.setAttribute('color', this.colAttr);
    geo.setAttribute('aFade', this.fadeAttr);
    geo.setAttribute('aSize', this.sizeAttr);
    geo.setDrawRange(0, 0);
    this.dustMat = new THREE.PointsNodeMaterial();
    this.dustMat.map = disc;
    this.dustMat.vertexColors = true;
    this.dustMat.transparent = true;
    this.dustMat.depthWrite = false;
    this.dustMat.sizeAttenuation = true;
    this.dustMat.opacityNode = attribute('aFade', 'float');
    this.dustMat.sizeNode = attribute('aSize', 'float');
    markMrtOverlay(this.dustMat);
    this.dust = new THREE.Points(geo, this.dustMat);
    this.dust.frustumCulled = false;
    this.dust.renderOrder = 5;
    // Hidden while the pool is empty: a 0-vertex draw is a WebGPU validation
    // notice ("Draw with a vertex count of 0 is unusual") on every ride start.
    this.dust.visible = false;
    world.add(this.dust);
  }

  /** Current exhaust length of the first nozzle (m) — the harness's engine gauge. */
  currentPlumeLength(): number { return this.plumeLen; }
  /** Live motes in the pool. */
  particleCount(): number { return this.alive; }

  update(inp: VehicleFxInputs): void {
    if (this.disposed) return;
    const dt = Math.min(0.1, Math.max(0, inp.dt));
    this.t += dt;
    // ── Engines: pilot glow when mounted, spool with throttle + speed, afterburner on boost/turbo ──
    const spool = Math.min(1, inp.speed / 60);
    const demand = Math.max(inp.throttle, spool * 0.6);           // coasting at speed still glows
    const burner = inp.boost || inp.turbo;
    const flicker = 1 + 0.10 * Math.sin(this.t * 47) + 0.06 * Math.sin(this.t * 113 + 1.7);
    const len = this.vis.plumeLen * (0.12 + 0.88 * demand) * (burner ? BOOST_PLUME_MULT : 1) * flicker;
    this.plumeLen = len;
    const tint = burner ? this.vis.boostColor : this.vis.color;
    for (const n of this.nozzles) {
      n.plume.scale.z = Math.max(0.001, len);
      n.plume.scale.x = n.plume.scale.y = burner ? 1.25 : 1;
      n.plumeMat.opacity = (0.22 + 0.5 * demand) * (burner ? 1.2 : 1);
      n.plumeMat.color.setHex(tint);
      n.coreMat.color.setHex(tint);
      n.core.scale.setScalar(this.vis.nozzleR * (1.6 + 1.4 * demand) * (burner ? 1.6 : 1) * flicker);
      n.flare.visible = burner;
      if (burner) { n.flare.scale.z = 0.35 + 0.25 * flicker; n.flareMat.opacity = 0.55 * flicker; }
    }
    // ── Dust / spray emission (world space) ──
    const dust = inp.grounded && !inp.onWater && inp.speed > HOVER_DUST_SPEED;
    const spray = inp.grounded && inp.onWater && inp.speed > HOVER_SPRAY_SPEED;
    if (dust || spray) {
      // The per-m/s ramp is divided by the speed scale too, so the curve still
      // reaches its cap at the craft's top speed rather than a quarter of the
      // way up it.
      const rate = dust
        ? Math.min(420, 40 + (inp.speed - HOVER_DUST_SPEED) * (5 / VEHICLE_SPEED_SCALE)) * this.vis.dust * (inp.drifting ? 1.8 : 1)
        : Math.min(300, 30 + inp.speed * (6 / VEHICLE_SPEED_SCALE));
      this.emitAcc += rate * dt;
      const n = Math.floor(this.emitAcc);
      if (n > 0) { this.emitAcc -= n; this.emit(n, inp, spray); }
    } else {
      this.emitAcc = 0;
    }
    // ── Integrate the pool (swap-remove the dead) ──
    let i = 0;
    while (i < this.alive) {
      const l = this.life[i] - dt;
      if (l <= 0) { this.swapRemove(i); continue; }
      this.life[i] = l;
      const k = i * 3;
      const drag = Math.max(0, 1 - DUST_DRAG * dt);
      this.vel[k] *= drag; this.vel[k + 2] *= drag;
      this.vel[k + 1] = this.vel[k + 1] * drag - DUST_GRAVITY * dt;
      this.pos[k] += this.vel[k] * dt; this.pos[k + 1] += this.vel[k + 1] * dt; this.pos[k + 2] += this.vel[k + 2] * dt;
      const u = l / this.maxLife[i];                     // 1 → 0
      this.fade[i] = 0.32 * Math.min(1, u * 2.5) * Math.min(1, (1 - u) * 6 + 0.35);
      this.size[i] = this.size[i] + dt * 0.9;            // motes bloom as they thin out
      i++;
    }
    this.dust.geometry.setDrawRange(0, this.alive);
    this.dust.visible = this.alive > 0;
    this.posAttr.needsUpdate = true; this.colAttr.needsUpdate = true;
    this.fadeAttr.needsUpdate = true; this.sizeAttr.needsUpdate = true;
  }

  private emit(n: number, inp: VehicleFxInputs, spray: boolean): void {
    this.hull.updateMatrixWorld(true);
    this.hull.matrixWorld.decompose(this._p, this._q, this._s);
    this._fwd.set(0, 0, -1).applyQuaternion(this._q);     // hull nose in world
    for (let j = 0; j < n; j++) {
      if (this.alive >= DUST_POOL) this.swapRemove(0);      // recycle the oldest
      const i = this.alive++;
      const k = i * 3;
      const src = this.dustPts[(j + i) % this.dustPts.length];
      this._v.copy(src).applyMatrix4(this.hull.matrixWorld);
      const jx = (Math.random() - 0.5) * 0.5, jz = (Math.random() - 0.5) * 0.5;
      this.pos[k] = this._v.x + jx; this.pos[k + 1] = this._v.y; this.pos[k + 2] = this._v.z + jz;
      // Thrown backward off the skirt, up and slightly outward; spray goes higher.
      const back = 2.5 + Math.random() * 4.5;
      const up = spray ? 2.5 + Math.random() * 3.5 : 0.8 + Math.random() * 2.2;
      const side = (Math.random() - 0.5) * 2.4;
      this.vel[k] = -this._fwd.x * back + this._fwd.z * side;
      this.vel[k + 1] = up;
      this.vel[k + 2] = -this._fwd.z * back - this._fwd.x * side;
      const life = spray ? 0.5 + Math.random() * 0.6 : 0.7 + Math.random() * 0.8;
      this.life[i] = life; this.maxLife[i] = life;
      this.size[i] = spray ? 0.35 + Math.random() * 0.4 : 0.3 + Math.random() * 0.5;
      this.fade[i] = 0;
      if (spray) { this.col[k] = 0.86; this.col[k + 1] = 0.93; this.col[k + 2] = 1.0; }
      else { const d = 0.9 + Math.random() * 0.2; this.col[k] = 0.62 * d; this.col[k + 1] = 0.54 * d; this.col[k + 2] = 0.40 * d; }
    }
  }

  private swapRemove(i: number): void {
    const last = this.alive - 1;
    if (i !== last) {
      const a = i * 3, b = last * 3;
      this.pos[a] = this.pos[b]; this.pos[a + 1] = this.pos[b + 1]; this.pos[a + 2] = this.pos[b + 2];
      this.vel[a] = this.vel[b]; this.vel[a + 1] = this.vel[b + 1]; this.vel[a + 2] = this.vel[b + 2];
      this.col[a] = this.col[b]; this.col[a + 1] = this.col[b + 1]; this.col[a + 2] = this.col[b + 2];
      this.life[i] = this.life[last]; this.maxLife[i] = this.maxLife[last];
      this.fade[i] = this.fade[last]; this.size[i] = this.size[last];
    }
    this.alive = last;
  }

  /** Tear down: nozzles come off the hull, the dust cloud leaves the world.
   *  Owned resources are released by the caller's deferral policy. */
  dispose(release: (r: { dispose(): void }) => void = (r) => r.dispose()): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const n of this.nozzles) {
      this.hull.remove(n.group);
      release(n.plume.geometry); release(n.flare.geometry);
      release(n.plumeMat); release(n.flareMat); release(n.coreMat);
    }
    this.nozzles.length = 0;
    this.world.remove(this.dust);
    release(this.dust.geometry); release(this.dustMat);
  }
}
